import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class GuidInsertService {
  private lastFocusedEl: HTMLElement | null = null;
  private lastSelectionText = '';

  setLastFocused(el: HTMLElement | null): void {
    if (el) {
      this.lastFocusedEl = el;
    }
  }

  setLastSelection(text: string | null | undefined): void {
    const normalized = (text || '').trim();
    if (normalized) {
      this.lastSelectionText = normalized;
    }
  }

  getLastSelection(): string {
    return this.lastSelectionText;
  }

  insertText(text: string): boolean {
    const el = this.lastFocusedEl || (document.activeElement as HTMLElement | null);
    if (!el) {
      return false;
    }

    const anyEl: any = el as any;
    const hasValue = typeof anyEl.value === 'string';
    const hasSelection = typeof anyEl.selectionStart === 'number' && typeof anyEl.selectionEnd === 'number';

    if (hasValue && hasSelection && typeof anyEl.setRangeText === 'function') {
      const start = anyEl.selectionStart ?? 0;
      const end = anyEl.selectionEnd ?? start;
      anyEl.setRangeText(text, start, end, 'end');
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    if ((el as any).isContentEditable) {
      const ok = document.execCommand('insertText', false, text);
      if (ok) {
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) {
        return false;
      }

      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(text));
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);

      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    return false;
  }

  copyToClipboard(text: string): Promise<boolean> {
    if ((navigator as any)?.clipboard?.writeText) {
      return (navigator as any).clipboard
        .writeText(text)
        .then(() => true)
        .catch(() => this.execCopy(text));
    }
    return this.execCopy(text);
  }

  private execCopy(text: string): Promise<boolean> {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    document.body.removeChild(textarea);
    return Promise.resolve(ok);
  }
}
